import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Video } from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import { getAllCameras } from "../../../service/securityService";
import type { Camera } from "../../../types/security_types";

const CameraLivePlayback: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cameraIdParam = searchParams.get("id") || "";

  const [loading, setLoading] = useState(true);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [mode, setMode] = useState<"live" | "playback">("live");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const list = await getAllCameras({ limit: 500 });
      setCameras(list);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load cameras");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(() => {
    if (!cameraIdParam) return cameras[0] || null;
    return (
      cameras.find((c) => (c._id || c.id) === cameraIdParam) ||
      cameras.find((c) => c.cameraId === cameraIdParam) ||
      null
    );
  }, [cameras, cameraIdParam]);

  useEffect(() => {
    if (!loading && cameras.length && !cameraIdParam && selected) {
      const sid = selected._id || selected.id;
      if (sid) setSearchParams({ id: sid }, { replace: true });
    }
  }, [loading, cameras, cameraIdParam, selected, setSearchParams]);

  const streamSrc =
    mode === "live" ? selected?.streamUrl?.trim() : selected?.playbackUrl?.trim();

  return (
    <div className="px-2 py-4 mt-14 min-h-screen flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate("/security/surveillance")}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Overview
        </button>
        <div className="flex items-center gap-3 flex-1 min-w-[200px]">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Video className="w-6 h-6 text-[#0fa9a6]" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Live view &amp; playback</h2>
            <p className="text-sm text-gray-600">
              Use stream and playback URLs from Camera master (MP4, WebRTC, or browser-supported
              sources).
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 flex-1 min-h-[420px]">
        <aside className="w-full lg:w-72 shrink-0 border border-gray-200 rounded-lg bg-white max-h-[480px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <SpinLoader />
            </div>
          ) : cameras.length === 0 ? (
            <p className="p-4 text-sm text-gray-500">No cameras registered.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {cameras.map((c) => {
                const id = c._id || c.id;
                const active = id === (selected?._id || selected?.id);
                return (
                  <li key={id || c.cameraId}>
                    <button
                      type="button"
                      onClick={() => id && setSearchParams({ id })}
                      className={`w-full text-left px-4 py-3 text-sm hover:bg-gray-50 ${
                        active ? "bg-[#EAFBFA] font-medium text-[#0b7d7b]" : "text-gray-800"
                      }`}
                    >
                      <div>{c.cameraName}</div>
                      <div className="text-xs text-gray-500">{c.cameraId}</div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        <section className="flex-1 flex flex-col gap-3 border border-gray-200 rounded-lg bg-white p-4">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setMode("live")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                mode === "live"
                  ? "bg-[#0fa9a6] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Live
            </button>
            <button
              type="button"
              onClick={() => setMode("playback")}
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                mode === "playback"
                  ? "bg-[#0fa9a6] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Playback
            </button>
          </div>

          {!selected ? (
            <p className="text-gray-500 text-sm">Select a camera.</p>
          ) : !streamSrc ? (
            <div className="flex-1 min-h-[320px] rounded-lg bg-gray-900 flex flex-col items-center justify-center text-gray-300 p-6 text-center">
              <p className="font-medium text-white mb-2">No URL configured</p>
              <p className="text-sm max-w-md">
                Add a {mode === "live" ? "stream" : "playback"} URL for{" "}
                <span className="text-white">{selected.cameraName}</span> in Camera master.
              </p>
            </div>
          ) : (
            <div className="flex-1 min-h-[320px] rounded-lg overflow-hidden bg-black">
              <video
                key={streamSrc}
                className="w-full h-full min-h-[320px] object-contain"
                src={streamSrc}
                controls
                playsInline
                muted
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default CameraLivePlayback;
